/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {getLanguage} from '../locale/locale';
import {GenericObject} from '../types/entities';
import {createRegExp} from './../utils/StringUtil';

// defines the reporting sisense option interface...
export interface SisenseOptions {
  baseReportURL?: string;
  reportRequestParameters?: string;
  ssoJwtUrl: string;
  jwtToken: string;
  logoutUrl?: string;
  themeId?: string;
  processingComplete?: boolean;
};

// defines the createEmbedPath function param definitions...
export interface CreateEmbedPathParams {
  options: SisenseOptions;
  reportRequestParameters?: string;
  dashboardId: string;
  widgetId?: string;
  isSessionCreated?: boolean;
};

// defines the Sisense Role enum...
export enum SisenseRole {
  Viewer = 'viewer',
  Designer = 'designer'
};

// defines the Sisense state variable that can be accessed...
export enum SisenseState {
  ServerOptions = 'serverOptions',
  SisenseOptionsError = 'sisenseOptionsError',
  SisenseLoading = 'sisenseLoading',
  SessionCreated = 'sessionCreated'
};

// iOn suite language ids and it respective sisense language ids...
export enum LanguageIds {
  en = 'en-US',
  'en-US' = 'en-US',
  it = 'it-IT',
  ja = 'ja-JP',
  fr = 'fr',
  'en-GB' = 'en-US',
  'es-ES' = 'es-ES',
  'es-MX' = 'es-ES',
  'pt-BR' = 'pt-BR'
};

export enum FolderId {
  AssetActivity = '64a79c11d6d989003643370d',
  CargoMonitoring = '649d5da354165d00344f2a3a',
  DashboardCharts = '64a79caed6d989003643370f',
  DriverActivityAndSafety = '64da0ac8b2250d0033ddfe62',
  EngineDiagnostic = '64a79d01d6d9890036433715',
  ExceptionManagement = '662f5561f8b30e0034caa20b',
  FleetDashboards = '6685236366aead0034508206',
  FuelAndEnergy = '64a79cded6d9890036433713',
  GeofenceReports = '64881a4254165d00344f1b25',
  Maintenance = '64a79d1ed6d9890036433717',
  NLQDMCTC = '67d2d03c2d20da0033f5e125',
  SafetyDashboard = '669f74bf66aead003450e72a',
  ThirdPartyIntegration = '64ed9eaeb2250d0033de0983',
  Trailer = '64a79d34d6d9890036433719',
  TripStopAndLocationReports = '662f57def8b30e0034caa212',
  Utilization = '669e92ce66aead003450e5a5',
  VehicleInspection = '67a3483d744c9000338159e8',
  MyReports = ''
};

export enum RootFolder {
  Basic = 'Common Reports', // Basic or Common
  Advanced = 'Special Reports' // Advanced or Special
};

export enum AdmRootFolder {
  Basic = 'ADM Reports',
  Advanced = 'Special Reports'
};

export enum AppState {
  Home = 'home',
  HomeFolder = 'home.folder',
  Dashboard = 'dashboard',
  WidgetEdit = 'widget.edit',
  WidgetAdd = 'widget.add',
};

export const FolderColors = {
  [FolderId.AssetActivity]              : 'caBlue',
  [FolderId.CargoMonitoring]            : 'caOrange',
  [FolderId.DashboardCharts]            : 'caGreen',
  [FolderId.DriverActivityAndSafety]    : 'caRed',
  [FolderId.EngineDiagnostic]           : 'electricPurple',
  [FolderId.ExceptionManagement]        : 'darkTeal',
  [FolderId.FleetDashboards]            : 'arcticBlue',
  [FolderId.FuelAndEnergy]              : 'grey3',
  [FolderId.GeofenceReports]            : 'caYellow',
  [FolderId.Maintenance]                : 'lightTeal',
  [FolderId.NLQDMCTC]                   : 'paleBlue',
  [FolderId.SafetyDashboard]            : 'skyBlue',
  [FolderId.ThirdPartyIntegration]      : 'lightBlue',
  [FolderId.Trailer]                    : 'caGreen',
  [FolderId.TripStopAndLocationReports] : 'caBlue',
  [FolderId.Utilization]                : 'grey2',
  [FolderId.VehicleInspection]          : 'caRed',
  [FolderId.MyReports]                  : 'caYellow'
};

/**
 * Get folder color by oid using lodash invert
 */
export const getFolderColorByOid = (oid: string): string => FolderColors[oid] ?? 'black';

/**
 * The method creating the sisense  embed url based on the dashboard and widget Id.
 * If the method just get the dashboard id, then it returns the dashboard embed url,
 * else it will returns the widget embed url.
 *
 * ```typescript
 *
 *  import {createEmbedPath} from '@ion/core/src/services/SisenseUtils';
 *
 *  // create the embed url for Sisense Dashboard
 *  const dashboardUrl = createEmbedPath({
 *    options          : this._serverOptions,
 *    isSessionCreated : this._isSessionCreated,
 *    dashboardId      : '61ae95d512b1a20037dbb6bd'
 *  });
 *
 *  // create the embed url for Sisense Widget
 *  const widgetUrl = createEmbedPath({
 *    options          : this._serverOptions,
 *    isSessionCreated : this._isSessionCreated,
 *    dashboardId      : '61ae95d512b1a20037dbb6bd',
 *    widgetId         ; '431ae35d512b1a20037dbb6bd'
 *  });
 *
 * ```
 */
export const createEmbedPath = ({
  options,
  reportRequestParameters,
  dashboardId,
  widgetId,
  isSessionCreated = true
}: CreateEmbedPathParams) => {
  if (options) {
    const {baseReportURL, ssoJwtUrl, reportRequestParameters: defaultParameters, jwtToken} = options;
    const lang = LanguageIds[getLanguage() || 'en'];
    let path   = `${baseReportURL}${dashboardId}`;
    if (widgetId) {
      path = `${path}/widgets/${widgetId}`;
    }
    path = `${path}?${reportRequestParameters || defaultParameters}&locale=${lang}`;
    // if the session is yet to be created, return the JWT URL to create a session to the client...
    if (!isSessionCreated) {
      path = `${ssoJwtUrl}?jwt=${jwtToken}&return_to=${encodeURIComponent(path)}`;
    }
    return path;
  }
};

/**
 * This removeThemeInURL is used to remove the theme query param from the iframe url. Basically,
 * this will be helpful to the Sisense SDK method of embedding without theme.
 *
 * @param url   The url to remove the theme
 * @param theme The theme id which come as a value to theme
 * @returns     the url that is removed the theme parameter
 *
 * ```typescript
 *
 *   import {removeThemeInURL} from '@ion/core/src/services/SisenseUtils';
 *
 *   const resovedURL = removeThemeInURL(url, theme);
 *
 * ```
 */
export const removeThemeInURL = (url: string, theme: string) => {
  const regex = createRegExp(`&theme=${theme}`);
  return url.replace(regex, '');
};

/**
 * The utility function help to construct the driver score which is
 * required format to the Report meta data server from the passsed
 * driver score from the CTC account entity.
 *
 * ```typescript
 *
 *  import {constructDriverScores} from '@ion/core/src/services/SisenseUtils';
 *
 *  const formattedScores = constructDriverScores(scores);
 *
 * ```
 */
export const constructDriverScores = (scores: GenericObject) => {
  const {
    Hard_Cornering_Weight: harsh_cornering_weight,
    Harsh_Braking_Weight: harsh_braking_weight,
    Harsh_Acceleration_Weight: harsh_accl_weight,
    Speed0: speeding0_weight,
    Speed1: speeding1_weight,
    Speed2: speeding2_weight,
    Speed3: speeding3_weight,
    Night_Weight: night_weight,
    Vision_Collision_Warning: vision_collision_warning,
    Vision_Distracted_Driving: vision_distracted_driving,
    Vision_Traffic_Violation: vision_traffic_violation,
    Vision_Lane_Drift: vision_lane_drift,
    Vision_Lane_Departure: vision_lane_departure
  } = scores || {};

  return {
    harsh_accl_weight,
    harsh_braking_weight,
    harsh_cornering_weight,
    speeding0_weight : speeding0_weight ?? 0,
    speeding1_weight,
    speeding2_weight,
    speeding3_weight,
    night_weight     : night_weight ?? 0,
    vision_collision_warning,
    vision_distracted_driving,
    vision_traffic_violation,
    vision_lane_drift,
    vision_lane_departure
  };
};
